import {
  Facebook,
  Linkedin,
  Youtube,
  Phone,
  Mail,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Images from "../../utils/Images";

// Custom X (Twitter) Icon Component - Official X logo shape
const XIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Custom TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Snapchat Ghost Icon - Official Snapchat logo
const SnapchatIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/>
  </svg>
);

export default function Footer() {
  const location = useLocation();
  
  // Check if we're on media-events or conference-trade-mission pages
  const isMediaEventsPage = location.pathname.startsWith("/media-events") || 
                            location.pathname.startsWith("/conference-trade-mission") ||
                            location.pathname.startsWith("/events") ||
                            location.pathname.startsWith("/event-details") ||
                            location.pathname.startsWith("/my-event-registrations") ||
                            location.pathname.startsWith("/my-registrations") ||
                            location.pathname.startsWith("/conference-registration") ||
                            location.pathname.startsWith("/conference-registrations") ||
                            location.pathname.startsWith("/media-events-overview") ||
                            location.pathname.startsWith("/help-center-media-events") ||
                            location.pathname.startsWith("/contact-us-media-events");
  
  // Determine Help Center and Contact Us links based on context
  const helpCenterLink = isMediaEventsPage ? "/help-center-media-events" : "/help-center";
  const contactUsLink = isMediaEventsPage ? "/contact-us-media-events" : "/contact-us";
  return (
    <footer className="relative bg-white border-t border-gray-200">
      <div className="px-4 py-12 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-5 lg:grid-cols-5">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            {Images.logo_colored ? (
              <img
                src={Images.logo_colored}
                alt="TripSecure"
                className="w-48"
              />
            ) : (
              <h3 className="mb-3 text-2xl font-semibold text-gray-900">
                <span>Trip</span>
                <span className="text-emerald-600">Secure</span>
              </h3>
            )}

            <p className="max-w-sm text-sm leading-relaxed text-gray-600">
              Travel Smart. Insure every road trip before your journey.
            </p>
          </div>

          {/* Support */}
          <div>
            <p className="mb-3 text-sm font-semibold tracking-wide text-gray-900">
              Support
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>
                <Link
                  to={helpCenterLink}
                  className="transition hover:text-emerald-600"
                >
                  Help Center
                </Link>
              </li>
              <li>
                <Link
                  to="/safety-information"
                  className="transition hover:text-emerald-600"
                >
                  Safety Information
                </Link>
              </li>
              <li>
                <Link
                  to={contactUsLink}
                  className="transition hover:text-emerald-600"
                >
                  Contact us
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="mb-3 text-sm font-semibold tracking-wide text-gray-900">
              Company
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>
                <Link
                  to="/about-us"
                  className="transition hover:text-emerald-600"
                  href="#"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="transition hover:text-emerald-600"
                  href="#"
                >
                  Privacy & Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-of-service"
                  className="transition hover:text-emerald-600"
                  href="#"
                >
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="mb-3 text-sm font-semibold tracking-wide text-gray-900">
              Contact
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex gap-2 items-center">
                <Phone size={16} />{" "}
                <Link
                  to={contactUsLink}
                  className="transition hover:text-emerald-600"
                >
                  Get in touch
                </Link>
              </li>
              <li>
                <Link
                  to="/become-a-provider"
                  className="transition hover:text-emerald-600"
                >
                  Become a Provider
                </Link>
              </li>
              <li>
                <Link
                  to="/become-an-agent"
                  className="transition hover:text-emerald-600"
                  href="#"
                >
                  Become an Agent
                </Link>
              </li>
            </ul>
          </div>

          {/* Social (as a grid column) */}
          <div>
            <p className="mb-3 text-sm font-semibold tracking-wide text-gray-900">
              Social
            </p>
            <div className="flex flex-wrap gap-3 text-gray-500">
              <a
                href="https://www.facebook.com/Tripsecureofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.youtube.com/@Tripsecureofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <Youtube size={18} />
              </a>
              <a
                href="https://www.tiktok.com/@tripsecureofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <TikTokIcon className="w-[18px] h-[18px]" />
              </a>
              <a
                href="https://www.snapchat.com/add/Tripsecureofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Snapchat"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <SnapchatIcon className="w-[18px] h-[18px]" />
              </a>
              <a
                href="https://x.com/tripsecureofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <XIcon className="w-[18px] h-[18px]" />
              </a>
              <a
                href="https://www.linkedin.com/company/tripsecure-gh"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="mailto:info@tripsecuregh.com"
                aria-label="Email"
                className="p-2 rounded-full transition hover:bg-emerald-50 hover:text-emerald-600"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative z-0 border-t border-gray-200">
        <div className="flex justify-between items-center px-4 py-6 mx-auto max-w-7xl text-sm text-gray-600 sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} Trip Secure. All Rights Reserved</p>
          <div className="hidden gap-6 sm:flex">
            <Link
              to="/privacy-policy"
              className="transition hover:text-emerald-600"
              href="#"
            >
              Privacy
            </Link>
            <Link
              to="/terms-of-service"
              className="transition hover:text-emerald-600"
              href="#"
            >
              Terms
            </Link>
            <Link
              to="/cookies"
              className="transition hover:text-emerald-600"
              href="#"
            >
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
